// `SettingsShell` — sub-navigation rail + content column (US-181).
//
// Per the prototype (`prototypes_and_docs/ls-app.jsx:1025-1046`):
//   - Fixed 184px left rail with a "PARAMÈTRES" eyebrow above 6 buttons.
//   - Active button uses `bg-bg-elevated` + primary text + weight 600.
//   - Click sets `section` and updates the URL `/settings/<id>` via
//     TanStack Router.
//
// Layout: the rail is rendered as a column inside the existing `_authed`
// shell. 5a.2 owns the AppShell mount; we render the rail + outlet
// directly so the route tree composes correctly without duplicating the
// outer `<AppShell>`.
//
// We use plain buttons (vs `<NavItem>`) because the DS NavItem mandates
// an icon slot — settings sub-nav is text-only by design (see proto).
// `// DS-GAP: SettingsSubNavItem` is *not* declared because this is one
// short surface; promoting to DS Wave E would over-fit.

import type { ReactNode } from 'react';

import { useT } from '@barel/i18n';
import { Button, FOCUS_RING_CLASS } from '@barel/ui';
import { useNavigate, useRouterState } from '@tanstack/react-router';

import { SETTINGS_SECTIONS } from './lib/sections';

export interface SettingsShellProps {
  children: ReactNode;
}

const FALLBACK_SECTION = SETTINGS_SECTIONS[0]?.id ?? 'profile';

/** Resolve the active section from `location.pathname` (`/settings/<id>`). */
function activeSectionFrom(pathname: string): string {
  const tail = pathname.replace(/^\/settings\/?/, '').split('/')[0] ?? '';
  if (!tail) return FALLBACK_SECTION;
  if (SETTINGS_SECTIONS.some((s) => s.id === tail)) return tail;
  return FALLBACK_SECTION;
}

const NAV_BUTTON_CLASS = [
  'group relative flex w-full items-center rounded-md px-3 py-2 text-left',
  'text-sm transition-colors duration-fast ease-out',
  'text-secondary hover:bg-bg-hover hover:text-primary',
  'data-[active=true]:bg-bg-elevated data-[active=true]:text-primary data-[active=true]:font-semibold',
  FOCUS_RING_CLASS,
].join(' ');

export function SettingsShell({ children }: SettingsShellProps) {
  const t = useT('settings');
  const navigate = useNavigate();
  const routerState = useRouterState();
  const active = activeSectionFrom(routerState.location.pathname);

  return (
    <div data-component="SettingsShell" className="flex min-h-[100vh] w-full bg-bg-base">
      <aside
        data-region="settings-subnav"
        aria-label={t('nav.title')}
        className="flex w-[184px] shrink-0 flex-col gap-1 border-r border-default bg-bg-surface px-2 py-5"
      >
        <div className="px-3 pb-2 text-2xs font-semibold uppercase tracking-wider text-tertiary">
          {t('nav.eyebrow')}
        </div>
        <nav className="flex flex-col gap-1" aria-label={t('nav.title')}>
          {SETTINGS_SECTIONS.map((section) => {
            const isActive = section.id === active;
            return (
              <button
                key={section.id}
                type="button"
                aria-current={isActive ? 'page' : undefined}
                data-active={isActive || undefined}
                data-testid={`settings-nav-${section.id}`}
                className={NAV_BUTTON_CLASS}
                onClick={() => {
                  void navigate({
                    to: '/settings/$section',
                    params: { section: section.path },
                  });
                }}
              >
                {t(`nav.${section.i18nKey}`)}
              </button>
            );
          })}
        </nav>
        <div className="mt-auto px-2 pt-4">
          <Button
            variant="ghost"
            size="sm"
            fullWidth
            onClick={() => {
              void navigate({ to: '/' });
            }}
            data-testid="settings-back"
          >
            ←
          </Button>
        </div>
      </aside>

      <section data-region="settings-content" className="min-w-0 flex-1 overflow-y-auto px-8 py-7">
        <div className="mx-auto w-full max-w-[640px]">{children}</div>
      </section>
    </div>
  );
}
